import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Agency, AppComment, CommentValue, DynamicControlType, DynamicFormSection, StepVerified } from '@app/models';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AppCommentsService {
  /** The constant name for the agencyUserComment. Use this variable instead of a string in case we need to replace this anywhere. */
  agencyUserCommentName = 'agencyUserComment';
  /** Get the agency user comment group for ease of use.  */
  get agencyUserCommentGroup(): FormGroup {
    return this.comments.get(this.agencyUserCommentName) as FormGroup;
  }

  qbatUserCommentName = 'qbatUserComment';
  get qbatUserCommentGroup(): FormGroup {
    return this.comments.get(this.qbatUserCommentName) as FormGroup;
  }

  /**
   * API payloads may omit `qbatUserComment` (e.g. legacy rows); QBAT Final Comments UI requires this group.
   * See QUA-1062.
   */
  ensureQbatUserCommentGroup(): FormGroup {
    const name = this.qbatUserCommentName;
    let g = this.comments.get(name) as FormGroup | undefined;
    if (!g) {
      g = this.createCommentControl(false, '', false, '', false, 'QBAT Final Comments');
      this.comments.addControl(name, g);
    }
    return g;
  }

  /**
   * Ensures the summary-section agency notes group exists (e.g. legacy FS apps before RFFR row creation).
   */
  ensureAgencyUserCommentGroup(agencyNotes = '', contractorComments = '', label = 'General Comments: '): FormGroup {
    const name = this.agencyUserCommentName;
    let g = this.comments.get(name) as FormGroup | undefined;
    if (!g) {
      g = this.createCommentControl(false, contractorComments, false, agencyNotes, false, label);
      this.comments.addControl(name, g);
    }
    return g;
  }

  colbiInternalNotesName = 'colbiInternalNotes';
  get colbiInternalNotesGroup(): FormGroup {
    return this.comments.get(this.colbiInternalNotesName) as FormGroup;
  }

  comments: FormGroup = new FormGroup({});
  /**
   * This is an array of objects that contain the name of the step, a boolean value indicating if the step is verified, and an object
   * containing the name of the section and a boolean value indicating if the section is verified.
   *
   * It is created in the AppService when calling appService.GetAgencyApp()
   *
   * This is used to show the "Step Verified" checkmark by each step in the QBAT User's app view, and the "Section Verified" checkmark too
   * To determine if an app is able to be acted upon, we use this.sectionVerified() instead.
   * We used this.allSectionsVerified() to determine if an app can be returned.
   */
  stepsVerified: StepVerified[] = [];

  constructor(private http: HttpClient) {}

  updateApplicationComment(appId: number, commentObj: AppComment | AppComment[]): Observable<string> {
    if (!Array.isArray(commentObj)) {
      commentObj = [commentObj];
    }

    const payload = {
      applicationId: appId,
      comments: commentObj,
    };

    return this.http.post<string>(`${environment.apiUrl}/api/agency/Application/SaveApplicationComment`, payload);
  }

  /**
   * Accepts a DynamicFormSection object, then finds the related comment FormGroup from the comments object and inserts a DynamicFormData object
   * into the section.formData array, with the comment FormGroup as it's value. This way we can avoid doing this work in the DynamicFormSectionCommentComponent
   * @param section
   */
  addCommentToFormData(section: DynamicFormSection, stepNumber: number, sectionNumber: number) {
    let commentGroup = this.comments?.get(section.formName) as FormGroup;

    if (!commentGroup) {
      commentGroup = this.createCommentControl();
      this.comments.addControl(section.formName, commentGroup);
    }

    commentGroup.get('label').setValue(section.name);
    commentGroup.get('step').setValue(stepNumber);
    commentGroup.get('section').setValue(sectionNumber);

    section.formData.push({
      controlName: section.formName,
      controlLabel: section.name,
      controlType: DynamicControlType.SectionComments,
      currentValue: commentGroup,
      hidden: false,
    });
  }

  /**
   * If the agency has no additonal requirements, preset the agency comments to be verified.
   * The user must be a qbat user and the app must be in submitted status.
   * */
  presetVerifiedAgencies(appAgencies: Agency[]): void {
    appAgencies?.forEach(appAgency => {
      let agencyComments = this.comments.controls[appAgency.formName] as FormGroup;
      if (!agencyComments) {
        agencyComments = this.createCommentControl(false, '', false, '', true, appAgency.name);
        this.comments.addControl(appAgency.formName, agencyComments);
      }
      if (!agencyComments.controls.verified.value && !appAgency.hasAddReqs) {
        agencyComments.controls.verified.setValue(true);
        agencyComments.markAsDirty();
      }
      this.updateStepsVerified(appAgency.formName, agencyComments.controls.verified.value, 'verified');
    });
  }

  fetchContractorComments(type: 'all' | 'agency' | 'section' = 'all'): string {
    let comments = '';
    const sortedControls = this.getSortedFilteredCommentValues(type);
    sortedControls
      .map(comment => {
        let fullText = '';
        if (comment.step == 0 && !comment.isAgency && comment.contractorComments) {
          fullText += 'General Comments: \n"' + comment.contractorComments + '"';
        } else {
          fullText += comment.isAgency ? 'Agency: ' + comment.label : 'Step ' + comment.step + ' - ' + comment.label;
          fullText += '\n"' + comment.contractorComments + '"';
        }
        return fullText;
      })
      .forEach((comment, index, arr) => {
        comments += comment ?? '';
        if (comment && index != arr.length - 1 && comments) {
          comments += '\n\n';
        }
      });
    return comments;
  }

  /**
   * Sorts comments according to the following rules:
   * 1. Agency comments come first
   * 2. General notes (step 0) come last
   * 3. Otherwise sort by step and section
   */
  sortComments(comments: CommentValue[]): CommentValue[] {
    return [...comments].sort((a, b) => {
      if (a.isAgency) return -1;
      if (b.isAgency) return 1;
      if (a.step == 0 && b.step != 0) return 1;
      if (b.step == 0 && a.step != 0) return -1;
      if (a.step == b.step) return a.section - b.section;
      return a.step - b.step;
    });
  }

  getSortedFilteredCommentValues(type: 'all' | 'agency' | 'section' = 'all'): CommentValue[] {
    const filteredControls = [];
    Object.keys(this.comments.controls).forEach(key => {
      const control = this.comments.controls[key].value as CommentValue;
      if (
        control.contractorComments &&
        (type == 'all' || (type == 'agency' && control.isAgency) || (type == 'section' && !control.isAgency))
      ) {
        filteredControls.push(control);
      }
    });
    return this.sortComments(filteredControls);
  }

  /**
   * Returns true if passed in section is verified or if not, returns true if all sections BESIDES AGENCY COMMENTS are verified.
   * @param formName Optionally provide the comment formname to check if only this section is verified.
   * @returns true if the specified section is verified, or returns true if no section is specified and all sections (besides agency comments) are verified.
   */
  sectionVerified(formName?: string): boolean {
    if (formName) {
      return this.comments.get([formName, 'verified'])?.value;
    } else {
      const keys = Object.keys(this.comments.controls);
      for (let i = 0; i < keys.length; i++) {
        if (
          keys[i] == this.agencyUserCommentName ||
          keys[i] == this.qbatUserCommentName ||
          keys[i] == this.colbiInternalNotesName
        ) {
          // If this is a comment field from the agency or qbat user, we don't care if it's verified or not.
          continue;
        }
        const comment = this.comments.controls[keys[i]] as FormGroup;
        if (!comment.controls.verified.value && !comment.controls.isAgency.value) {
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Unlike this.sectionVerified(), this ONLY returns true if all sections including Agency Comments are verified.
   * @returns true if every section is verified. Else false.
   */
  allSectionsVerified(): boolean {
    const keys = Object.keys(this.comments.controls);
    for (let i = 0; i < keys.length; i++) {
      const comment = this.comments.controls[keys[i]] as FormGroup;
      if (!comment.controls.verified?.value) {
        return false;
      }
    }
    return true;
  }

  /**
   * Used to update the section values for this.stepsVerified whenever an approved value changes
   * and have it update the step's allVerified value.
   * This is used for QBAT Users' app views to show a "Step Verified" checkmark by each step.
   */
  updateStepsVerified(sectionName: string, value: boolean, fieldToUpdate: 'verified' | 'areaOfConcern' | 'hasNote') {
    for (let i = 0; i < this.stepsVerified.length; i++) {
      const step = this.stepsVerified[i];
      const sections = Object.keys(step.sections);
      if (sections.includes(sectionName)) {
        if (fieldToUpdate == 'verified') {
          step.sections[sectionName].verified = value;
          step.verified = value ? Object.values(step.sections).every(section => section.verified) : false;
        } else if (fieldToUpdate == 'areaOfConcern') {
          step.sections[sectionName].areaOfConcern = value;
          step.areaOfConcern = value ? true : Object.values(step.sections).some(section => section.areaOfConcern);
        } else if (fieldToUpdate == 'hasNote') {
          step.sections[sectionName].hasNote = value;
          step.hasNote = value ? true : Object.values(step.sections).some(section => section.hasNote);
        }

        return;
        // // For the Agency Selection step:
        // if (i == 0) {
        //   const sectionsVerified = Object.values(step.sections);
        //   // QUA-610 - Agency Selection step must have every agency verified for the step to be verified - even though we don't need to have every agency verified in order to act on the app.
        //   // We previously set `step.verified` to be `isAgencyComment ? sectionsVerified.some(val => val) : sectionsVerified.every(val => val);`
        //   if (fieldToUpdate === 'verified') {
        //     step.verified = sectionsVerified.every(val => val);
        //   }
        //   return;
        // }

        // const agencyHasAoC = Object.entries(this.stepsVerified[i].sections).some(([key, val]) => {
        //   const control = this.comments.get(key) as FormGroup;

        //   return (
        //     control?.controls?.isAgency?.value === true &&
        //     control?.controls?.areaOfConcern?.value === true &&
        //     val === true
        //   );
        // });

        // if (fieldToUpdate === 'areaOfConcern') {
        //   this.stepsVerified[i].areaOfConcern = sectionsVerified.some(val => val === true) || agencyHasAoC;
        // }

        // if (i === 0) {
        //   const step1AgencyAoC = Object.keys(this.stepsVerified[i].sections).some(key => {
        //     const control = this.comments.get(key) as FormGroup;

        //     return control?.controls?.isAgency?.value === true && control?.controls?.areaOfConcern?.value === true;
        //   });

        //   this.stepsVerified[i].areaOfConcern = this.stepsVerified[i].areaOfConcern || step1AgencyAoC;
        // }

        // break;
      }
    }
  }

  /**
   * Goes through a list of app agencies and gets an array of Ids of the agencies that are verified.
   * If `unverified` is true, this function instead returns the opposite: an array of Ids of the agencies that are UNVERIFIED.
   */
  fetchVerifiedAgencyIds(appAgencies: Agency[], unverified = false): number[] {
    const verifiedAgencyIds: number[] = [];

    appAgencies.forEach(appAgency => {
      if ((this.comments.controls[appAgency.agencyToken] as FormGroup)?.controls.verified.value == !unverified) {
        verifiedAgencyIds.push(appAgency.id);
      }
    });

    return verifiedAgencyIds;
  }

  /**
   * @description Get each changed comment from the current step we're on.
   *
   *  Then we create an API call to save/update each of these comments and return an array of these calls ready to be streamed.
   *
   * @param step - The index of the step formGroup inside the main formArray.
   * @returns An array of observables that can will save a specific comment when zipped together and streamed.
   */
  getChangedCommentsObservable(appId: number): Observable<any> {
    const changedCommentsControls = this.getChangedComments(this.comments);

    return this.updateApplicationComment(appId, changedCommentsControls);
  }

  createCommentControl(
    verified = false,
    comments = '',
    areaOfConcern = false,
    notes = '',
    isAgency = false,
    label = ''
  ) {
    return new FormGroup({
      verified: new FormControl(verified),
      contractorComments: new FormControl(comments),
      areaOfConcern: new FormControl(areaOfConcern),
      agencyNotes: new FormControl(notes),
      isAgency: new FormControl(isAgency),
      label: new FormControl(label),
      step: new FormControl(0),
      section: new FormControl(0),
    });
  }

  /**
   * Collects every comment the user has edited so we can save them (e.g. when clicking Next).
   * Uses getRawValue() instead of .value so locked fields still get included —
   * otherwise a locked Verified checkbox can be left out of the payload and saved as unchecked.
   */
  private getChangedComments(commentsGroup: FormGroup): AppComment[] {
    const changedComments = [];
    Object.keys(commentsGroup.controls).forEach(key => {
      if (commentsGroup.controls[key].dirty) {
        const comment: AppComment = (commentsGroup.controls[key] as FormGroup).getRawValue();
        comment.formName = key;
        changedComments.push(comment);
      }
    });
    return changedComments;
  }
}
